// src/components/FacebookAd.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import './AdminDashboard.css';
import './AdminDashboardModern.css';

const CHANNELS = {
  facebook: {
    name: 'Facebook',
    subtitle: 'Create and manage Facebook ad campaigns with advanced targeting',
    creativeLabel: 'Facebook ad',
    platform: 'Facebook',
    icon: '📘'
  },
  google: {
    name: 'Google',
    subtitle: 'Create and manage Google Ads campaigns with advanced targeting',
    creativeLabel: 'Google ad',
    platform: 'Google Ads',
    icon: '🔍'
  }
};

const FacebookAd = ({ channel = 'facebook' }) => {
  const navigate = useNavigate();
  const channelConfig = CHANNELS[channel] || CHANNELS.facebook;

  React.useEffect(() => {
    fetchAds();
    fetchCreatives();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- route wrappers mount a fixed channel

  React.useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchCreatives = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/ads/v1/get-creatives/${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCreatives({
          images: data.images || [],
          videos: data.videos || []
        });
      }
    } catch (error) {
      console.error('Error fetching creatives:', error);
    }
  };

  const fetchAds = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/ads/v1/get-ads/${SITE_CONFIG.SITE_ID}?channel=${channel}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAds(data.ads || []);
      } else {
        console.error('Failed to fetch ads');
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };
  const [formData, setFormData] = useState({
    ad_set_name: '',
    lifetime_budget: '',
    start_date: '',
    end_date: '',
    ad_description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatives, setCreatives] = useState({ images: [], videos: [] });
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [activeTab, setActiveTab] = useState('images');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [adAcknowledged, setAdAcknowledged] = useState(false);
  const [showFacebookAccessGate, setShowFacebookAccessGate] = useState(channel === 'facebook');
  const [pageUrl, setPageUrl] = useState('');
  const [pageStatus, setPageStatus] = useState('idle');
  const [pageMessage, setPageMessage] = useState('');
  const [requestingAccess, setRequestingAccess] = useState(false);

  const ONBOARDING_API = 'https://tophamtechbeb.com/api/toptech/onboarding/v1';

  const readPageStatus = React.useCallback(async () => {
    try {
      const response = await fetch(`${ONBOARDING_API}/fb-page/status/${SITE_CONFIG.SITE_ID}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });
      if (!response.ok) return null;
      const result = await response.json();
      return result.data || null;
    } catch (error) {
      return null;
    }
  }, []);

  // Show where they left off - the approval happens on Facebook and can sit
  // unanswered for days, so this has to survive them closing the tab.
  React.useEffect(() => {
    if (channel !== 'facebook') return;
    readPageStatus().then((data) => {
      if (!data) return;
      if (data.fb_page_url) setPageUrl(data.fb_page_url);
      if (['connected', 'pending', 'resolving'].includes(data.fb_page_status)) {
        setPageStatus(data.fb_page_status);
      }
    });
  }, [channel, readPageStatus]);

  // While waiting on their approval, check until Facebook reports it granted.
  React.useEffect(() => {
    if (pageStatus !== 'pending' && pageStatus !== 'resolving') return undefined;
    const timer = setInterval(async () => {
      const data = await readPageStatus();
      if (!data?.fb_page_status) return;
      if (data.fb_page_status !== pageStatus) {
        setPageStatus(data.fb_page_status);
        if (data.fb_page_status === 'connected') setPageMessage('');
      }
    }, 20000);
    return () => clearInterval(timer);
  }, [pageStatus, readPageStatus]);

  const handleRequestPageAccess = async () => {
    setRequestingAccess(true);
    setPageMessage('');
    try {
      const response = await fetch(`${ONBOARDING_API}/fb-page/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'treas-api-key-2025'
        },
        body: JSON.stringify({ site_id: SITE_CONFIG.SITE_ID, page_url: pageUrl })
      });
      const result = await response.json();

      if (response.ok) {
        setPageStatus(result.fb_page_status || 'pending');
      } else {
        setPageStatus('error');
        setPageMessage(result.message || result.error || 'That request did not go through. Try again.');
      }
    } catch (error) {
      setPageStatus('error');
      setPageMessage('We could not reach Facebook. Check your connection and try again.');
    } finally {
      setRequestingAccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/ads/v1/set-ad', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'treas-api-key-2025'
        },
        body: JSON.stringify({
          ...formData,
          email: JSON.parse(localStorage.getItem('user'))?.email || '',
          agent_name: JSON.parse(localStorage.getItem('user'))?.username || '',
          site_id: SITE_CONFIG.SITE_ID,
          channel,
          ad_set_id: '',
          image: selectedCreative?.type === 'image' ? selectedCreative.file_id : '',
          video: selectedCreative?.type === 'video' ? selectedCreative.file_id : '',
          lifetime_budget: parseFloat(formData.lifetime_budget)
        })
      });

      if (response.ok) {
        setShowSuccessModal(true);
        setFormData({
          ad_set_name: '',
          lifetime_budget: '',
          start_date: '',
          end_date: '',
          ad_description: ''
        });
        setSelectedCreative(null);
        setCurrentStep(1);
        setActiveTab('images');
        setAdAcknowledged(false);
        fetchAds(); // Refresh ads list
      } else {
        alert('Failed to submit ad request');
      }
    } catch (error) {
      console.error('Error submitting ad request:', error);
      alert('Failed to submit ad request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-dashboard-modern">
      {/* Header */}
      <header className="admin-header-modern">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">{channelConfig.name} Advertising</h1>
            <p className="dashboard-subtitle">{channelConfig.subtitle}</p>
          </div>
          <div className="header-right">
            <button onClick={() => navigate('/admin/digital-ads')} className="back-home-btn-modern">
              <span className="btn-icon">←</span>
              Back to Digital Ads
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="admin-main-content">
        {/* Campaign Creation Section */}
        <div className="campaign-creation-section">
          <div className="section-header">
            <h2 className="section-title">Create New Campaign</h2>
            <div className="step-indicator-modern">
              <div className={`step-modern ${currentStep >= 1 ? 'active' : ''} ${currentStep === 1 ? 'current' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-label">Creative</div>
              </div>
              <div className="step-connector"></div>
              <div className={`step-modern ${currentStep >= 2 ? 'active' : ''} ${currentStep === 2 ? 'current' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-label">Details</div>
              </div>
              <div className="step-connector"></div>
              <div className={`step-modern ${currentStep >= 3 ? 'active' : ''} ${currentStep === 3 ? 'current' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-label">Review</div>
              </div>
            </div>
          </div>
        
          <div className="form-container-modern">
            <form onSubmit={handleSubmit} className="facebook-ad-form-modern">
          {currentStep === 1 && (
            <div className="step-content">
              <h2>Choose Your Creative</h2>
              <p className="step-description">Select an image or video for your {channelConfig.creativeLabel}</p>
              
              <div className="creative-tabs">
                <div className="tab-header">
                  <button 
                    type="button" 
                    className={`tab-btn ${activeTab === 'images' ? 'active' : ''}`}
                    onClick={() => setActiveTab('images')}
                  >
                    Images ({creatives.images.length})
                  </button>
                  <button 
                    type="button" 
                    className={`tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('videos')}
                  >
                    Videos ({creatives.videos.length})
                  </button>
                </div>
                
                <div className="creative-grid">
                  {activeTab === 'images' && creatives.images.map((image) => (
                    <div 
                      key={image.file_id} 
                      className={`creative-card ${selectedCreative?.file_id === image.file_id ? 'selected' : ''}`}
                      onClick={() => setSelectedCreative(image)}
                    >
                      <img src={image.image_url} alt="Creative" />
                      <div className="creative-overlay">
                        {selectedCreative?.file_id === image.file_id && <span>✓</span>}
                      </div>
                    </div>
                  ))}
                  {activeTab === 'videos' && creatives.videos.map((video) => (
                    <div 
                      key={video.file_id} 
                      className={`creative-card ${selectedCreative?.file_id === video.file_id ? 'selected' : ''}`}
                      onClick={() => setSelectedCreative(video)}
                    >
                      <video 
                        src={video.video_url} 
                        controls
                        preload="metadata"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                        onLoadedMetadata={(e) => {
                          e.target.currentTime = 1; // Show first frame
                        }}
                      />
                      <div className="creative-overlay">
                        {selectedCreative?.file_id === video.file_id && <span>✓</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="step-actions">
                <button 
                  type="button" 
                  className="next-btn" 
                  disabled={!selectedCreative}
                  onClick={() => setCurrentStep(2)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}
          
          {currentStep === 2 && (
            <div className="step-content">
              <h2>Campaign Details</h2>
              <p className="step-description">Set up your ad campaign parameters</p>
              
              <div className="form-grid">
                <div className="form-field">
                  <label>Campaign Name</label>
                  <input
                    type="text"
                    placeholder="Enter campaign name"
                    value={formData.ad_set_name}
                    onChange={(e) => setFormData({...formData, ad_set_name: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-field">
                  <label>Budget ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    max="250"
                    placeholder="Max $250"
                    value={formData.lifetime_budget}
                    onChange={(e) => setFormData({...formData, lifetime_budget: e.target.value})}
                    required
                  />
                  {formData.lifetime_budget > 250 && (
                    <span className="error-text">Maximum budget is $250</span>
                  )}
                </div>
                
                <div className="form-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    min={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-field">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-field full-width">
                  <label>Ad Description</label>
                  <textarea
                    placeholder="Describe your ad..."
                    value={formData.ad_description}
                    onChange={(e) => setFormData({...formData, ad_description: e.target.value})}
                    rows="3"
                    required
                  />
                </div>
              </div>
              
              <div className="step-actions">
                <button type="button" className="back-btn" onClick={() => setCurrentStep(1)}>Back</button>
                <button type="button" className="next-btn" onClick={() => setCurrentStep(3)}>Review</button>
              </div>
            </div>
          )}
          
          {currentStep === 3 && (
            <div className="step-content">
              <h2>Review & Submit</h2>
              <p className="step-description">Review your ad before submitting</p>
              
              <div className="review-section">
                <div className="review-creative">
                  <h3>Selected Creative</h3>
                  {selectedCreative?.type === 'image' ? (
                    <img src={selectedCreative.image_url} alt="Selected" className="review-media" />
                  ) : (
                    <video src={selectedCreative?.video_url} className="review-media" controls />
                  )}
                </div>
                
                <div className="review-details">
                  <h3>Campaign Details</h3>
                  <div className="detail-item">
                    <span>Campaign:</span> {formData.ad_set_name}
                  </div>
                  <div className="detail-item">
                    <span>Budget:</span> ${formData.lifetime_budget}
                  </div>
                  <div className="detail-item">
                    <span>Duration:</span> {formData.start_date} to {formData.end_date}
                  </div>
                  <div className="detail-item">
                    <span>Description:</span> {formData.ad_description}
                  </div>
                </div>
              </div>
              
              <div className="ad-acknowledgment-section">
                <label className="ad-checkbox-label">
                  <input
                    type="checkbox"
                    checked={adAcknowledged}
                    onChange={(e) => setAdAcknowledged(e.target.checked)}
                    required
                  />
                  <span className="ad-checkmark"></span>
                  <span className="ad-checkbox-text">
                    {channelConfig.name} Ad Campaigns can take 24-48 hours to process and initiate on the platform.
                    Upon submission your ad will start in "Requested" status, move to "Pending" once we have submitted the ad, 
                    and then to "Active" once the start date activates it (you can find it below). 
                    Be advised there is a $25 fee for submitting a {channelConfig.name} ad. This will be charged in addition to the Budget set for the ad in the next 72 hours. Please reach out with any questions.
                  </span>
                </label>
              </div>
              
              <div className="step-actions">
                <button type="button" className="back-btn" onClick={() => setCurrentStep(2)}>Back</button>
                <button type="submit" className="submit-btn" disabled={submitting || !adAcknowledged}>
                  {submitting ? 'Creating Ad...' : `Create ${channelConfig.name} Ad`}
                </button>
              </div>
            </div>
          )}
            </form>
          </div>
        </div>
      
        {/* Campaigns Dashboard */}
        <div className="campaigns-dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Your {channelConfig.name} Campaigns</h2>
            <div className="stats-row">
              <div className="stat-card-small">
                <div className="stat-number">{ads.length}</div>
                <div className="stat-label">Total</div>
              </div>
              <div className="stat-card-small">
                <div className="stat-number">{ads.filter(ad => ad.status === 'active').length}</div>
                <div className="stat-label">Active</div>
              </div>
              <div className="stat-card-small">
                <div className="stat-number">{ads.filter(ad => !ad.status || ad.status === 'pending').length}</div>
                <div className="stat-label">Pending</div>
              </div>
            </div>
          </div>
        
          <div className="campaigns-content">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading your campaigns...</p>
              </div>
            ) : ads.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h3>No campaigns yet</h3>
                <p>Create your first {channelConfig.name} ad campaign to get started</p>
              </div>
            ) : (
              <div className="campaigns-grid-modern">
            {ads.map((ad, index) => {
              const status = ad.status || 'pending';
              const statusConfig = {
                active: { icon: '🟢', color: '#28a745', bg: '#d4edda' },
                pending: { icon: '🟡', color: '#ffc107', bg: '#fff3cd' },
                paused: { icon: '⏸️', color: '#6c757d', bg: '#e2e3e5' },
                completed: { icon: '✅', color: '#17a2b8', bg: '#d1ecf1' }
              };
              const config = statusConfig[status] || statusConfig.pending;
              
              return (
                <div key={index} className="ad-card">
                  <div className="card-header">
                    <div className="ad-title">
                      <h3>{ad.ad_set_name}</h3>
                      <div className="status-badge" style={{backgroundColor: config.bg, color: config.color}}>
                        <span className="status-icon">{config.icon}</span>
                        <span className="status-text">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-content">
                    <div className="budget-section">
                      <div className="budget-amount">${ad.lifetime_budget}</div>
                      <div className="budget-label">Lifetime Budget</div>
                    </div>
                    
                    <div className="date-range">
                      <div className="date-item">
                        <span className="date-icon">📅</span>
                        <div>
                          <div className="date-value">{new Date(ad.start_date).toLocaleDateString()}</div>
                          <div className="date-label">Start Date</div>
                        </div>
                      </div>
                      <div className="date-separator">→</div>
                      <div className="date-item">
                        <span className="date-icon">🏁</span>
                        <div>
                          <div className="date-value">{new Date(ad.end_date).toLocaleDateString()}</div>
                          <div className="date-label">End Date</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="metrics-section">
                      <div className="metrics-grid">
                        <div className="metric-item">
                          <div className="metric-value">{ad.impressions || '0'}</div>
                          <div className="metric-label">Impressions</div>
                        </div>
                        <div className="metric-item">
                          <div className="metric-value">{ad.clicks || '0'}</div>
                          <div className="metric-label">Clicks</div>
                        </div>
                        <div className="metric-item">
                          <div className="metric-value">${ad.spend || '0.00'}</div>
                          <div className="metric-label">Spend</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="description-section">
                      <div className="description-label">Campaign Description</div>
                      <div className="description-text">{ad.ad_description}</div>
                    </div>
                  </div>
                  
                  <div className="card-footer">
                    <div className="campaign-type">
                      <span className="type-icon">{channelConfig.icon}</span>
                      <span>{channelConfig.name} Campaign</span>
                    </div>
                  </div>
                </div>
              );
            })}
              </div>
            )}
          </div>
        </div>
      </main>

      {channel === 'facebook' && showFacebookAccessGate && (
        <div className="modal-overlay-modern" onClick={() => navigate('/admin/digital-ads')}>
          <div
            className="modal-modern facebook-access-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="facebook-access-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2 id="facebook-access-title" className="modal-title">Grant Facebook Page access</h2>
                <p className="facebook-access-subtitle">Complete these steps before continuing to Facebook Advertising.</p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Return to Digital Ads"
                onClick={() => navigate('/admin/digital-ads')}
              >
                ×
              </button>
            </div>

            <div className="modal-body facebook-access-body">
              {pageStatus === 'connected' ? (
                <div className="facebook-access-details">
                  <div className="facebook-access-details-header">
                    <span className="facebook-access-details-icon" aria-hidden="true">✓</span>
                    <div>
                      <h3>Your Page is connected</h3>
                      <p>We can run ads on your Page and send leads straight to you.</p>
                    </div>
                  </div>
                </div>
              ) : pageStatus === 'resolving' ? (
                <div className="facebook-access-details">
                  <div className="facebook-access-details-header">
                    <span className="facebook-access-details-icon" aria-hidden="true">f</span>
                    <div>
                      <h3>Looking up your Page</h3>
                      <p>We are finding your Page on Facebook. This usually takes a minute - you can leave this open.</p>
                    </div>
                  </div>
                </div>
              ) : pageStatus === 'pending' ? (
                <>
                  <div className="facebook-access-details">
                    <div className="facebook-access-details-header">
                      <span className="facebook-access-details-icon" aria-hidden="true">f</span>
                      <div>
                        <h3>Approve the request on Facebook</h3>
                        <p>We sent it to the admins of your Page. This screen updates on its own.</p>
                      </div>
                    </div>
                  </div>

                  <ol className="facebook-access-steps">
                    <li>Open Facebook and switch into your Page.</li>
                    <li>Go to <strong>Settings &amp; privacy → Settings</strong>.</li>
                    <li>Select <strong>Page setup</strong>, then <strong>Page access</strong>.</li>
                    <li>Under <strong>Partners with access</strong>, find <strong>Pending access requests</strong>.</li>
                    <li>Next to <strong>Agent Winds</strong>, choose <strong>Review request</strong> and approve it.</li>
                  </ol>

                  <p className="facebook-access-permission-note">
                    You may also get a notification that Agent Winds requested access. It arrives on your
                    own profile rather than your Page, and it only tells you about the request - you still
                    approve it from the settings above.
                  </p>

                  <p className="facebook-access-permission-note">
                    The request asks for Manage, Ads and Insights. Facebook only lets us receive your
                    leads automatically if we have Manage, so it is included. Your Page stays yours -
                    you can remove our access at any time from this same screen.
                  </p>

                  <p className="facebook-access-permission-note">
                    Not seeing it? The request goes to whoever is an admin of the Page. If your brokerage
                    or a past marketer set the Page up, ask them to approve it.
                  </p>
                </>
              ) : (
                <>
                  <div className="facebook-access-details">
                    <div className="facebook-access-details-header">
                      <span className="facebook-access-details-icon" aria-hidden="true">f</span>
                      <div>
                        <h3>Connect your Facebook Page</h3>
                        <p>We will send you an access request. You approve it on Facebook, and that is it.</p>
                      </div>
                    </div>
                  </div>

                  <div className="form-field facebook-access-field">
                    <label htmlFor="facebook-page-url">Your Facebook Page address</label>
                    <input
                      id="facebook-page-url"
                      type="text"
                      value={pageUrl}
                      onChange={(event) => setPageUrl(event.target.value)}
                      placeholder="facebook.com/YourPageName"
                    />
                  </div>

                  <details className="facebook-access-alternative">
                    <summary>Not sure which address to use?</summary>
                    <div className="facebook-access-alternative-content">
                      <ol>
                        <li>
                          Open <a href="https://business.facebook.com/latest/settings" target="_blank" rel="noopener noreferrer">Meta Business Suite settings</a> while
                          logged in as the Page owner.
                        </li>
                        <li>Choose <strong>Pages</strong>, then select your Page.</li>
                        <li>Copy the long number shown as <strong>Page ID</strong>.</li>
                      </ol>
                      <p>
                        You can also open your Page, go to <strong>About</strong>, and look for the Page ID
                        there. You will only see it while signed in as an admin of the Page.
                      </p>
                    </div>
                  </details>

                  <p className="facebook-access-permission-note">
                    Facebook needs the number, not the web address. Use your business Page, not your
                    personal profile - a profile cannot run Page ads.
                  </p>

                  {pageStatus === 'error' && pageMessage && (
                    <p className="facebook-access-permission-note" role="alert">{pageMessage}</p>
                  )}

                  <div className="facebook-access-details-header">
                    <span className="facebook-permission-chip">✓ Manage</span>
                    <span className="facebook-permission-chip">✓ Ads</span>
                    <span className="facebook-permission-chip">✓ Insights</span>
                    <span className="facebook-access-permission-note">Needed to receive your leads</span>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => navigate('/admin/digital-ads')}>
                Back to Digital Ads
              </button>
              {pageStatus === 'connected' ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setShowFacebookAccessGate(false)}
                >
                  Continue to Facebook Ads
                </button>
              ) : pageStatus === 'resolving' ? (
                <button type="button" className="btn-primary" disabled>
                  Looking up your Page…
                </button>
              ) : pageStatus === 'pending' ? (
                <button type="button" className="btn-primary" disabled>
                  Waiting for your approval…
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!pageUrl.trim() || requestingAccess}
                  onClick={handleRequestPageAccess}
                >
                  {requestingAccess ? 'Sending…' : 'Send access request'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay-modern" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-modern success-modal-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="success-icon-modern">✓</div>
              <h3 className="modal-title">Campaign Submitted Successfully!</h3>
            </div>
            
            <div className="modal-body">
              <p className="success-message">
                Your {channelConfig.name} ad campaign has been successfully submitted and will be posted to {channelConfig.platform} within the next 48 hours.
              </p>
              <p className="success-note">
                Our team will reach out to you if any questions arise during the setup process.
              </p>
              <div className="success-footer">
                Thank you for your business.
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowSuccessModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacebookAd;
